import { Eye, Pencil, Trash2, Users } from "lucide-react";
import { useState } from "react";

import { StatusPill } from "@/components/status-pill";
import { Button } from "@/components/ui/button";
import type { AdminUserSummary } from "@/lib/api";

import { UserDeleteDialog } from "./user-delete-dialog";
import { UserDetailDialog } from "./user-detail-dialog";
import { UserEditDialog } from "./user-edit-dialog";

interface UserTableProps {
  users: AdminUserSummary[];
}

export function UserTable({ users }: UserTableProps) {
  const [viewUserId, setViewUserId] = useState<string | null>(null);
  const [editUserId, setEditUserId] = useState<string | null>(null);
  const [deleteUser, setDeleteUser] = useState<AdminUserSummary | null>(null);

  return (
    <>
      <div className="bg-card border-border overflow-hidden rounded-lg border shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-warm-gray-01 border-border border-b">
              <tr>
                <th className="text-muted px-4 py-3 text-[10px] font-medium tracking-[0.16em] uppercase">
                  Name
                </th>
                <th className="text-muted px-4 py-3 text-[10px] font-medium tracking-[0.16em] uppercase">
                  Email
                </th>
                <th className="text-muted px-4 py-3 text-[10px] font-medium tracking-[0.16em] uppercase">
                  Role
                </th>
                <th className="text-muted px-4 py-3 text-[10px] font-medium tracking-[0.16em] uppercase">
                  Joined
                </th>
                <th className="text-muted px-4 py-3 text-right text-[10px] font-medium tracking-[0.16em] uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-border divide-y">
              {users.length === 0 ? (
                <tr>
                  <td colSpan={5}>
                    <div className="flex flex-col items-center justify-center gap-1.5 px-6 py-12 text-center">
                      <Users className="text-muted h-6 w-6" />
                      <p className="text-foreground text-sm font-medium">No users found</p>
                      <p className="text-muted max-w-xs text-xs">
                        Invite a teammate, or adjust your search to see more people.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id} className="hover:bg-warm-gray-01 transition-colors">
                    <td className="px-4 py-3 align-middle font-medium">
                      {user.first_name || user.last_name
                        ? `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim()
                        : "—"}
                    </td>
                    <td className="text-muted px-4 py-3 align-middle">{user.email}</td>
                    <td className="px-4 py-3 align-middle">
                      <StatusPill tone={user.role === "admin" ? "accent" : "neutral"}>
                        {user.role}
                      </StatusPill>
                    </td>
                    <td className="text-muted px-4 py-3 align-middle">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-right align-middle">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="View user"
                          onClick={() => setViewUserId(user.id)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="Edit user"
                          onClick={() => setEditUserId(user.id)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="Delete user"
                          className="text-destructive hover:text-destructive"
                          onClick={() => setDeleteUser(user)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <UserDetailDialog
        userId={viewUserId}
        open={!!viewUserId}
        onOpenChange={(open) => !open && setViewUserId(null)}
      />
      <UserEditDialog
        userId={editUserId}
        open={!!editUserId}
        onOpenChange={(open) => !open && setEditUserId(null)}
      />
      <UserDeleteDialog
        user={deleteUser}
        open={!!deleteUser}
        onOpenChange={(open) => !open && setDeleteUser(null)}
      />
    </>
  );
}
